import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiError } from '../../lib/api';
import { editableSections, sectionMeta, getDefaults, applyContent } from '../../mock';
import ValueEditor from './ValueEditor';
import Logo from '../../components/Logo';
import { useToast } from '../../hooks/use-toast';
import { Inbox, LayoutList, LogOut, Mail, Trash2, RotateCcw, Save, ExternalLink, Check } from 'lucide-react';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('enquiries');
  const [ready, setReady] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [content, setContent] = useState({});
  const [active, setActive] = useState('projects');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEnquiries = useCallback(async () => {
    const { data } = await api.get('/enquiries');
    setEnquiries(data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await api.get('/auth/me');
        const overrides = (await api.get('/content')).data;
        setContent({ ...getDefaults(), ...overrides });
        await loadEnquiries();
        setReady(true);
      } catch (err) {
        localStorage.removeItem('mm_token');
        navigate('/admin/login');
      }
    })();
  }, [navigate, loadEnquiries]);

  useEffect(() => {
    if (content[active] !== undefined) setDraft(JSON.parse(JSON.stringify(content[active])));
  }, [active, content]);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* token may be stale */ }
    localStorage.removeItem('mm_token');
    navigate('/admin/login');
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/${active}`, { data: draft });
      setContent((c) => ({ ...c, [active]: draft }));
      applyContent({ [active]: draft });
      toast({ title: 'Published', description: `${sectionMeta[active].label} is now live on the site.` });
    } catch (err) {
      toast({ title: 'Could not publish', description: apiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const resetSection = async () => {
    setSaving(true);
    try {
      await api.delete(`/content/${active}`);
      const original = getDefaults()[active];
      setContent((c) => ({ ...c, [active]: original }));
      setDraft(JSON.parse(JSON.stringify(original)));
      applyContent({ [active]: original });
      toast({ title: 'Reverted to original content' });
    } catch (err) {
      toast({ title: 'Could not revert', description: apiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const markRead = async (id) => { await api.patch(`/enquiries/${id}/read`); loadEnquiries(); };
  const removeEnquiry = async (id) => { await api.delete(`/enquiries/${id}`); loadEnquiries(); };

  if (!ready) {
    return <div className="min-h-screen bg-[var(--mm-cream)] flex items-center justify-center text-sm text-[var(--mm-ink)]/60" data-testid="admin-loading">Loading Content Studio…</div>;
  }

  const unread = enquiries.filter((e) => !e.read).length;

  return (
    <div className="min-h-screen bg-[var(--mm-cream)]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[var(--mm-line)]">
        <div className="max-w-[1240px] mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5 min-w-0">
            <Logo />
            <span className="hidden sm:inline mm-kicker">Content Studio</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" data-testid="view-site-link"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[var(--mm-ink)]/70 hover:text-[var(--mm-purple)] px-3 py-2">
              View site <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-[var(--mm-line)] hover:border-[var(--mm-purple)]">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1240px] mx-auto px-5 py-8 md:py-12">
        <div className="flex gap-2 mb-8">
          <TabBtn active={tab === 'enquiries'} onClick={() => setTab('enquiries')} icon={Inbox} testid="tab-enquiries">
            Enquiries{unread > 0 && <span className="ml-2 text-[11px] bg-[var(--mm-purple)] text-white rounded-full px-2 py-0.5">{unread}</span>}
          </TabBtn>
          <TabBtn active={tab === 'content'} onClick={() => setTab('content')} icon={LayoutList} testid="tab-content">Site content</TabBtn>
        </div>

        {tab === 'enquiries' ? (
          <div className="space-y-4" data-testid="enquiries-list">
            {enquiries.length === 0 && (
              <div className="bg-white border border-[var(--mm-line)] rounded-2xl p-10 text-center text-sm text-[var(--mm-ink)]/60" data-testid="enquiries-empty">
                No enquiries yet. Submissions from the contact page will appear here.
              </div>
            )}
            {enquiries.map((e) => (
              <article key={e.id} data-testid={`enquiry-${e.id}`}
                className={`bg-white border rounded-2xl p-5 md:p-6 ${e.read ? 'border-[var(--mm-line)]' : 'border-[var(--mm-purple)]/40'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="mm-kicker">{e.interest || 'General enquiry'}</div>
                    <h3 className="text-lg font-semibold text-[var(--mm-ink)] mt-1 break-words">{e.name}</h3>
                    <div className="text-sm text-[var(--mm-ink)]/70 mt-1 break-words">
                      {e.email}{e.phone ? ` · ${e.phone}` : ''}{e.org ? ` · ${e.org}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={`mailto:${e.email}`} className="w-9 h-9 rounded-full border border-[var(--mm-line)] flex items-center justify-center text-[var(--mm-purple)]"><Mail className="w-4 h-4" /></a>
                    {!e.read && (
                      <button onClick={() => markRead(e.id)} data-testid={`mark-read-${e.id}`}
                        className="w-9 h-9 rounded-full border border-[var(--mm-line)] flex items-center justify-center text-[var(--mm-ink)]/70"><Check className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => removeEnquiry(e.id)} data-testid={`delete-enquiry-${e.id}`}
                      className="w-9 h-9 rounded-full border border-[var(--mm-line)] flex items-center justify-center text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {e.message && <p className="mt-4 text-sm leading-relaxed text-[var(--mm-ink)]/80 whitespace-pre-line break-words">{e.message}</p>}
                <div className="mt-4 text-xs text-[var(--mm-ink)]/45">
                  {new Date(e.created_at).toLocaleString()} · {e.email_sent ? 'Notification emailed' : 'Email notification failed'}
                  {e.autoreply_sent === false && <span className="text-amber-600"> · confirmation to sender not sent</span>}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-6" data-testid="content-editor">
            <nav className="bg-white border border-[var(--mm-line)] rounded-2xl p-3 h-fit lg:sticky lg:top-24">
              <ul className="space-y-1 max-h-[60vh] lg:max-h-none overflow-y-auto">
                {editableSections.map((s) => (
                  <li key={s}>
                    <button onClick={() => setActive(s)} data-testid={`section-${s}`}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${active === s ? 'bg-[var(--mm-purple)] text-white' : 'hover:bg-[var(--mm-cream)] text-[var(--mm-ink)]/80'}`}>
                      <span className="block font-semibold">{sectionMeta[s]?.label || s}</span>
                      <span className={`block text-[11px] mt-0.5 ${active === s ? 'text-white/70' : 'text-[var(--mm-ink)]/45'}`}>{sectionMeta[s]?.page}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <section className="bg-white border border-[var(--mm-line)] rounded-2xl p-5 md:p-7 min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-[var(--mm-line)]">
                <div>
                  <div className="mm-kicker">{sectionMeta[active]?.page}</div>
                  <h2 className="text-lg md:text-xl font-semibold text-[var(--mm-ink)]">{sectionMeta[active]?.label}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={resetSection} disabled={saving} data-testid="reset-section-btn"
                    className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-[var(--mm-line)] hover:border-[var(--mm-purple)] disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Revert
                  </button>
                  <button onClick={save} disabled={saving} data-testid="save-section-btn" className="btn-primary disabled:opacity-60">
                    {saving ? 'Publishing…' : 'Publish'} <Save className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="pt-6 min-w-0">
                {draft !== null && <ValueEditor value={draft} onChange={setDraft} path={active} />}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${active ? 'bg-[var(--mm-purple)] text-white' : 'bg-white border border-[var(--mm-line)] text-[var(--mm-ink)]/70 hover:border-[var(--mm-purple)]'}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}
